/**
 * EntityMultiPicker (P3-A) — multi-select async với chip display.
 *
 * Use cases: notification rule recipients (multi user), workflow notify
 * channels (multi channel), form field options (multi tag).
 */
import { Check, Loader2, Plus, Search, X } from "lucide-react";
import * as React from "react";

import type { PickerItem, PickerKind } from "@/api/picker";
import { Input } from "@/components/ui/input";
import { usePickerOptions } from "@/hooks/usePickerOptions";
import { cn } from "@/lib/utils";

interface Props {
  kind: PickerKind;
  value: string[];
  onChange: (ids: string[], items: PickerItem[]) => void;
  parentId?: string;
  filters?: Record<string, unknown>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Max chips to display before truncate. */
  maxDisplay?: number;
  /** Hint khi empty. */
  hint?: string;
}

export function EntityMultiPicker({
  kind,
  value,
  onChange,
  parentId,
  filters,
  placeholder = "Thêm...",
  disabled = false,
  className,
  maxDisplay = 5,
  hint,
}: Props): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [knownItems, setKnownItems] = React.useState<Map<string, PickerItem>>(
    new Map(),
  );
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { items, loading, error } = usePickerOptions(kind, {
    q: query,
    parentId,
    filters,
    enabled: open,
  });

  // Sync knownItems khi fetch trả về
  React.useEffect(() => {
    if (items.length === 0) return;
    setKnownItems((prev) => {
      const next = new Map(prev);
      for (const it of items) next.set(it.id, it);
      return next;
    });
  }, [items]);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = (item: PickerItem): void => {
    const has = value.includes(item.id);
    const nextIds = has ? value.filter((id) => id !== item.id) : [...value, item.id];
    const nextMap = new Map(knownItems);
    nextMap.set(item.id, item);
    setKnownItems(nextMap);
    const nextItems = nextIds
      .map((id) => nextMap.get(id))
      .filter((x): x is PickerItem => x !== undefined);
    onChange(nextIds, nextItems);
  };

  const remove = (id: string): void => {
    const nextIds = value.filter((v) => v !== id);
    const nextItems = nextIds
      .map((vid) => knownItems.get(vid))
      .filter((x): x is PickerItem => x !== undefined);
    onChange(nextIds, nextItems);
  };

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        disabled={disabled}
        className={cn(
          "w-full min-h-8 flex items-center gap-1 flex-wrap px-1.5 py-1 rounded-md border bg-background text-sm",
          "hover:border-primary/50 transition-colors text-left",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          open && "border-primary ring-1 ring-primary/30",
        )}
      >
        {value.length === 0 ? (
          <span className="text-muted-foreground text-xs px-1">{placeholder}</span>
        ) : null}
        {value.slice(0, maxDisplay).map((id) => {
          const it = knownItems.get(id);
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-xs"
            >
              <span className="truncate max-w-[120px]">{it?.label ?? id}</span>
              {!disabled ? (
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(id);
                  }}
                  className="hover:text-destructive shrink-0"
                  aria-label={`Remove ${id}`}
                >
                  <X className="size-3" />
                </span>
              ) : null}
            </span>
          );
        })}
        {value.length > maxDisplay ? (
          <span className="text-[10px] text-muted-foreground px-1">
            +{value.length - maxDisplay}
          </span>
        ) : null}
        <Plus className="size-3.5 text-muted-foreground ml-auto shrink-0" />
      </button>

      {open ? (
        <div className="absolute z-[55] mt-1 w-full max-h-72 overflow-y-auto rounded-md border bg-popover shadow-lg animate-fade-in">
          <div className="flex items-center gap-2 border-b px-2 py-1.5 sticky top-0 bg-popover">
            <Search className="size-3.5 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Tìm..."
              className="h-7 border-0 shadow-none focus-visible:ring-0 px-0 text-xs"
            />
            {loading ? (
              <Loader2 className="size-3.5 text-muted-foreground animate-spin shrink-0" />
            ) : null}
          </div>

          {error ? (
            <div className="px-3 py-3 text-xs text-destructive">{error}</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground">
              {loading ? "Đang tải..." : "Không có kết quả."}
            </div>
          ) : (
            items.map((it) => {
              const checked = value.includes(it.id);
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => toggle(it)}
                  className={cn(
                    "w-full flex items-start gap-2 px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted/50",
                    checked && "bg-primary/5",
                  )}
                >
                  <span
                    className={cn(
                      "size-3.5 mt-0.5 shrink-0",
                      checked ? "text-primary" : "text-transparent",
                    )}
                  >
                    <Check className="size-3.5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{it.label}</div>
                    {it.secondary ? (
                      <div className="text-[10px] text-muted-foreground truncate">
                        {it.secondary}
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })
          )}
        </div>
      ) : null}

      {hint && !open ? (
        <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>
      ) : null}
    </div>
  );
}
